import { useState } from "react";
import { useUserStats } from "../lib/UserStatsContext";
import { isSubscriptionActive, FREE_TIER_LIMITS, FEATURE_NAMES } from "../lib/userStats";
import type { UsageFeature } from "../lib/userStats";
import { createCheckoutSession, createBillingPortalSession } from "../lib/subscription";
import PromoCodeInput from "./PromoCodeInput";

interface ProfileAccountTabProps {
  checkoutMessage: { type: "success" | "cancelled"; text: string } | null;
}

export default function ProfileAccountTab({ checkoutMessage }: ProfileAccountTabProps) {
  const { stats, monthlyUsage, refreshStats } = useUserStats();
  const [billingLoading, setBillingLoading] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);

  return (
    <>
      {/* Subscription */}
      <div className="mb-8">
        <div className="section-label">Subscription</div>

        {/* Checkout Result Message */}
        {checkoutMessage && (
          <div className={`mb-4 p-3 rounded-md border ${
            checkoutMessage.type === "success"
              ? "border-accent/30 bg-accent/5"
              : "border-border bg-bg"
          }`}>
            <p className={`text-sm font-[family-name:--font-ui] ${
              checkoutMessage.type === "success" ? "text-accent" : "text-muted"
            }`}>
              {checkoutMessage.text}
            </p>
          </div>
        )}

        {/* Current Plan */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-text font-semibold font-[family-name:--font-ui]">
              {stats?.subscriptionTier === "premium" && isSubscriptionActive(stats)
                ? "Premium"
                : stats?.subscriptionTier === "trial" && isSubscriptionActive(stats)
                ? "Premium Trial"
                : "Free plan"}
            </span>
            {stats?.subscriptionTier === "premium" && isSubscriptionActive(stats) && !stats.subscriptionCancelAtPeriodEnd && (
              <span className="text-xs text-accent font-[family-name:--font-ui] font-medium">(active)</span>
            )}
            {stats?.subscriptionTier === "premium" && isSubscriptionActive(stats) && stats.subscriptionCancelAtPeriodEnd && (
              <span className="text-xs text-gi-medium font-[family-name:--font-ui] font-medium">(cancels soon)</span>
            )}
            {stats?.subscriptionTier === "trial" && isSubscriptionActive(stats) && (
              <span className="text-xs text-gi-medium font-[family-name:--font-ui] font-medium">(trial)</span>
            )}
          </div>
          {stats?.subscriptionTier === "premium" && isSubscriptionActive(stats) && stats.subscriptionCancelAtPeriodEnd && stats.subscriptionPeriodEnd && (
            <p className="text-sm text-muted font-[family-name:--font-ui]">
              Access until {new Date(stats.subscriptionPeriodEnd).toLocaleDateString()}
            </p>
          )}
          {stats?.subscriptionTier === "premium" && isSubscriptionActive(stats) && !stats.subscriptionCancelAtPeriodEnd && stats.subscriptionPeriodEnd && (
            <p className="text-sm text-muted font-[family-name:--font-ui]">
              Renews {new Date(stats.subscriptionPeriodEnd).toLocaleDateString()}
            </p>
          )}
          {stats?.subscriptionExpiry && isSubscriptionActive(stats) && stats.subscriptionTier === "trial" && (
            <p className="text-sm text-muted font-[family-name:--font-ui]">
              Trial ends {new Date(stats.subscriptionExpiry).toLocaleDateString()}
            </p>
          )}
          {(!stats?.subscriptionTier || stats.subscriptionTier === "free" || !isSubscriptionActive(stats)) && (
            <p className="text-sm text-muted font-[family-name:--font-ui]">Limited monthly usage on free features</p>
          )}
        </div>

        {/* Monthly Usage - Free users only */}
        {(!stats?.subscriptionTier || stats.subscriptionTier === "free" || !isSubscriptionActive(stats)) && monthlyUsage && (
          <div className="mb-4">
            <p className="text-sm text-muted font-[family-name:--font-ui] mb-3">This month's usage</p>
            <div className="space-y-3">
              {(["chat", "mealBuilder", "recipeTransformer", "menuDecoder"] as UsageFeature[]).map((feature) => {
                const limit = FREE_TIER_LIMITS[feature];
                if (limit === 0) return null;
                const current = monthlyUsage[feature] || 0;
                const percentage = (current / limit) * 100;
                return (
                  <div key={feature}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-muted font-[family-name:--font-ui]">{FEATURE_NAMES[feature]}</span>
                      <span className="text-xs text-muted font-[family-name:--font-ui]">{current}/{limit}</span>
                    </div>
                    <div className="w-full h-1.5 bg-border rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          percentage >= 100 ? "bg-gi-high" : percentage >= 80 ? "bg-gi-medium" : "bg-accent"
                        }`}
                        style={{ width: `${Math.min(100, percentage)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted font-[family-name:--font-ui] mt-3">Resets on the 1st of each month</p>
          </div>
        )}

        {/* Actions based on subscription status */}
        {stats?.subscriptionTier === "premium" && isSubscriptionActive(stats) ? (
          <button
            onClick={async () => {
              setBillingLoading(true);
              try {
                const { url } = await createBillingPortalSession(window.location.href);
                window.location.href = url;
              } catch (err) {
                console.error("Failed to open billing portal:", err);
                alert("Unable to open billing portal. Please try again.");
              } finally {
                setBillingLoading(false);
              }
            }}
            disabled={billingLoading}
            className="w-full py-2.5 border border-border rounded-md font-[family-name:--font-ui] text-sm font-medium hover:bg-black/5 disabled:opacity-50 flex items-center justify-center gap-2 text-text"
          >
            {billingLoading ? (
              <>
                <div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                Loading...
              </>
            ) : stats?.subscriptionCancelAtPeriodEnd ? (
              "Reactivate or Manage Subscription"
            ) : (
              "Manage Subscription"
            )}
          </button>
        ) : (
          <div className="space-y-4">
            {/* Upgrade Button */}
            <button
              onClick={async () => {
                setUpgradeLoading(true);
                try {
                  const { url } = await createCheckoutSession(
                    `${window.location.origin}/profile?checkout=success`,
                    `${window.location.origin}/profile?checkout=cancelled`
                  );
                  window.location.href = url;
                } catch (err) {
                  console.error("Checkout error:", err);
                  alert("Unable to start checkout. Please try again.");
                } finally {
                  setUpgradeLoading(false);
                }
              }}
              disabled={upgradeLoading}
              className="w-full py-2.5 bg-accent text-white rounded-md font-[family-name:--font-ui] text-sm font-medium hover:opacity-85 active:opacity-75 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {upgradeLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Processing...
                </>
              ) : (
                "Upgrade to Premium - $4.99/mo"
              )}
            </button>

            {/* Promo Code Section */}
            <div className="pt-4 border-t border-border-light">
              <p className="text-sm text-muted font-[family-name:--font-ui] mb-3">Have a promo code?</p>
              <PromoCodeInput
                compact
                onSuccess={() => {
                  refreshStats();
                }}
              />
            </div>
          </div>
        )}

        {/* Premium Benefits */}
        {(!stats?.subscriptionTier || stats.subscriptionTier === "free" || !isSubscriptionActive(stats)) && (
          <div className="mt-4 pt-4 border-t border-border-light">
            <p className="text-sm text-muted font-[family-name:--font-ui] mb-2">Premium includes:</p>
            <ul className="text-sm text-text font-[family-name:--font-ui] space-y-1">
              <li>- Unlimited chat</li>
              <li>- Unlimited meals</li>
              <li>- Menu decoder</li>
              <li>- Priority AI</li>
            </ul>
          </div>
        )}
      </div>

      {/* Sign Out */}
      <div className="border-t border-border-light pt-4 mt-4">
        <button
          onClick={() => {
            import("firebase/auth").then(({ getAuth, signOut }) => {
              signOut(getAuth());
            });
          }}
          className="text-sm text-muted font-[family-name:--font-ui] font-medium hover:text-text transition-colors"
        >
          Sign Out
        </button>
      </div>
    </>
  );
}
